/**
 * MEVIA - Action Plan Dashboard
 * Dashboard for admin to manage and track action plans
 * Vista tipo lista con filtros
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  ClipboardList,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Pencil,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Building,
  User,
  RefreshCw,
  Download,
  ArrowLeft,
  Users,
  TrendingUp,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import axios from 'axios';
import logger from '../utils/logger';

// Status configuration with colors and icons
const STATUS_CONFIG = {
  draft: {
    es: 'Borrador',
    en: 'Draft',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: FileText
  },
  active: {
    es: 'Activo',
    en: 'Active',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Clock
  },
  pending_validation: {
    es: 'Por Validar',
    en: 'Pending Validation',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: AlertCircle
  },
  validated: {
    es: 'Validado',
    en: 'Validated',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle
  },
  closed: {
    es: 'Cerrado',
    en: 'Closed',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: CheckCircle
  }
};

const ActionPlanDashboard = ({ onBack }) => {
  const { language } = useLanguage();
  const { hasAccess } = useSubscription();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Translations
  const t = {
    title: language === 'es' ? 'Planes de Acción' : 'Action Plans',
    subtitle: language === 'es' 
      ? 'Gestiona y da seguimiento a los planes de desarrollo de tu equipo' 
      : 'Manage and track your team\'s development plans',
    back: language === 'es' ? 'Volver' : 'Back',
    search: language === 'es' ? 'Buscar por nombre o departamento...' : 'Search by name or department...',
    allStatus: language === 'es' ? 'Todos los estados' : 'All statuses',
    allTypes: language === 'es' ? 'Todos los tipos' : 'All types',
    allCategories: language === 'es' ? 'Todas las categorías' : 'All categories',
    catCultural: language === 'es' ? 'Brecha Cultural' : 'Cultural Gap',
    catNom035: language === 'es' ? 'NOM-035' : 'NOM-035',
    individual: language === 'es' ? 'Individual' : 'Individual',
    company: language === 'es' ? 'Empresarial' : 'Company',
    employee: language === 'es' ? 'Empleado' : 'Employee',
    department: language === 'es' ? 'Departamento' : 'Department',
    status: language === 'es' ? 'Estado' : 'Status',
    progress: language === 'es' ? 'Progreso' : 'Progress',
    actions: language === 'es' ? 'Acciones' : 'Actions',
    view: language === 'es' ? 'Ver Detalles' : 'View Details',
    edit: language === 'es' ? 'Editar' : 'Edit',
    validate: language === 'es' ? 'Validar' : 'Validate',
    exportPdf: language === 'es' ? 'Exportar PDF' : 'Export PDF',
    generatePlan: language === 'es' ? 'Generar Plan' : 'Generate Plan',
    generateCompanyPlan: language === 'es' ? 'Generar Plan Empresarial' : 'Generate Company Plan',
    noPlans: language === 'es' ? 'No hay planes de acción' : 'No action plans found',
    noPlansDesc: language === 'es' 
      ? 'Genera planes de acción para tus empleados basados en sus evaluaciones.' 
      : 'Generate action plans for your employees based on their assessments.',
    totalPlans: language === 'es' ? 'Total de Planes' : 'Total Plans',
    activeCount: language === 'es' ? 'Activos' : 'Active',
    pendingCount: language === 'es' ? 'Por Validar' : 'Pending',
    completedCount: language === 'es' ? 'Completados' : 'Completed',
    employeesReady: language === 'es' ? 'Empleados Listos' : 'Employees Ready',
    employeesReadyDesc: language === 'es' 
      ? 'Empleados con evaluaciones completadas sin plan asignado' 
      : 'Employees with completed evaluations without assigned plan',
    selectEmployee: language === 'es' ? 'Seleccionar Empleado' : 'Select Employee',
    selectEmployeeDesc: language === 'es' 
      ? 'Selecciona un empleado para generar su plan de acción personalizado' 
      : 'Select an employee to generate their personalized action plan',
    generate: language === 'es' ? 'Generar' : 'Generate',
    cancel: language === 'es' ? 'Cancelar' : 'Cancel',
    generating: language === 'es' ? 'Generando...' : 'Generating...',
    planGenerated: language === 'es' ? 'Plan generado exitosamente' : 'Plan generated successfully',
    errorGenerating: language === 'es' ? 'Error al generar el plan' : 'Error generating plan',
    refresh: language === 'es' ? 'Actualizar' : 'Refresh',
    createdAt: language === 'es' ? 'Creado' : 'Created',
    type: language === 'es' ? 'Tipo' : 'Type',
    items: language === 'es' ? 'tareas' : 'tasks'
  };

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/action-plans/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      logger.error('Error loading dashboard:', error);
      toast.error(language === 'es' ? 'Error al cargar el dashboard' : 'Error loading dashboard');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Filter plans
  const filteredPlans = dashboardData?.plans?.filter(plan => {
    const matchesSearch = 
      (plan.employee_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (plan.department?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (plan.title?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || plan.status === statusFilter;
    const matchesType = typeFilter === 'all' || plan.plan_type === typeFilter;
    const matchesCategory = categoryFilter === 'all' || (plan.plan_category || 'cultural') === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesType && matchesCategory;
  }) || [];

  const handleGenerateIndividualPlan = async () => {
    if (!selectedEmployee) return;
    
    try {
      setGeneratingPlan(true);
      const response = await axios.post(
        `/action-plans/generate/individual/${selectedEmployee.id}?language=${language}`
      );
      
      toast.success(t.planGenerated);
      setShowGenerateDialog(false);
      setSelectedEmployee(null);
      loadDashboard();
      
      // Navigate to the new plan
      navigate(`/action-plans/${response.data.id}`);
    } catch (error) {
      logger.error('Error generating plan:', error);
      const errorMsg = error.response?.data?.detail || t.errorGenerating;
      toast.error(errorMsg);
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleGenerateCompanyPlan = async () => {
    try {
      setGeneratingPlan(true);
      const response = await axios.post(`/action-plans/generate/company?language=${language}`);
      
      toast.success(t.planGenerated);
      loadDashboard();
      
      // Navigate to the new plan
      navigate(`/action-plans/${response.data.id}`);
    } catch (error) {
      logger.error('Error generating company plan:', error);
      const errorMsg = error.response?.data?.detail || t.errorGenerating;
      toast.error(errorMsg);
    } finally {
      setGeneratingPlan(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const StatusIcon = config.icon;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <StatusIcon className="h-3 w-3" />
        {config[language]}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const summary = dashboardData?.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t.back}
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-teal-600" />
              {t.title}
            </h2>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadDashboard}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t.refresh}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" disabled={generatingPlan}>
                <Plus className="h-4 w-4 mr-2" />
                {generatingPlan ? t.generating : t.generatePlan}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowGenerateDialog(true)}>
                <User className="h-4 w-4 mr-2" />
                {t.individual}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleGenerateCompanyPlan}>
                <Building className="h-4 w-4 mr-2" />
                {t.generateCompanyPlan}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Target className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.total_plans || 0}</p>
                <p className="text-sm text-gray-500">{t.totalPlans}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.active || 0}</p>
                <p className="text-sm text-gray-500">{t.activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.pending_validation || 0}</p>
                <p className="text-sm text-gray-500">{t.pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{(summary.validated || 0) + (summary.closed || 0)}</p>
                <p className="text-sm text-gray-500">{t.completedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.employees_without_plans || 0}</p>
                <p className="text-sm text-gray-500">{t.employeesReady}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t.search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={t.allStatus} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allStatus}</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config[language]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder={t.allTypes} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allTypes}</SelectItem>
                <SelectItem value="individual">{t.individual}</SelectItem>
                <SelectItem value="company">{t.company}</SelectItem>
              </SelectContent>
            </Select>
            {/* M-07: Category filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                <SelectValue placeholder={t.allCategories} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allCategories}</SelectItem>
                <SelectItem value="cultural">{t.catCultural}</SelectItem>
                <SelectItem value="nom035">{t.catNom035}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Plans Table */}
      {filteredPlans.length === 0 ? (
        <Card className="border-2 border-dashed border-gray-200">
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.noPlans}</h3>
            <p className="text-gray-500 mb-6">{t.noPlansDesc}</p>
            <Button onClick={() => setShowGenerateDialog(true)} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="h-4 w-4 mr-2" />
              {t.generatePlan}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.employee}</TableHead>
                  <TableHead>{t.type}</TableHead>
                  <TableHead>{t.department}</TableHead>
                  <TableHead>{t.status}</TableHead>
                  <TableHead>{t.progress}</TableHead>
                  <TableHead>{t.createdAt}</TableHead>
                  <TableHead className="text-right">{t.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPlans.map((plan) => (
                  <TableRow key={plan.id} className="cursor-pointer hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-teal-100 flex items-center justify-center">
                          {plan.plan_type === 'company' ? (
                            <Building className="h-4 w-4 text-teal-600" />
                          ) : (
                            <User className="h-4 w-4 text-teal-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {plan.employee_name || (plan.plan_type === 'company' ? (language === 'es' ? 'Plan Empresarial' : 'Company Plan') : '-')}
                          </p>
                          <p className="text-sm text-gray-500">{plan.title}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5 flex-wrap">
                        <Badge variant="outline" className={plan.plan_type === 'company' ? 'border-purple-300 text-purple-700' : 'border-teal-300 text-teal-700'}>
                          {plan.plan_type === 'company' ? t.company : t.individual}
                        </Badge>
                        {/* M-07: Category label */}
                        <Badge className={(plan.plan_category || 'cultural') === 'nom035' 
                          ? 'bg-orange-100 text-orange-700 border-orange-300' 
                          : 'bg-green-100 text-green-700 border-green-300'} 
                          variant="outline" data-testid={`category-badge-${plan.id}`}>
                          {(plan.plan_category || 'cultural') === 'nom035' ? 'NOM-035' : (language === 'es' ? 'Cultural' : 'Cultural')}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {plan.department || '-'}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(plan.status)}
                    </TableCell>
                    <TableCell>
                      <div className="w-32">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{plan.progress_percentage}%</span>
                          <span className="text-xs text-gray-500">
                            {plan.completed_items}/{plan.total_items} {t.items}
                          </span>
                        </div>
                        <Progress value={plan.progress_percentage} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {formatDate(plan.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/action-plans/${plan.id}`)}>
                            <Eye className="h-4 w-4 mr-2" />
                            {t.view}
                          </DropdownMenuItem>
                          {plan.status === 'draft' && (
                            <DropdownMenuItem onClick={() => navigate(`/action-plans/${plan.id}?edit=true`)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              {t.edit}
                            </DropdownMenuItem>
                          )}
                          {plan.status === 'pending_validation' && (
                            <DropdownMenuItem onClick={() => navigate(`/action-plans/${plan.id}?validate=true`)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              {t.validate}
                            </DropdownMenuItem>
                          )}
                          {hasAccess('pdf_export') && (
                            <DropdownMenuItem onClick={() => navigate(`/action-plans/${plan.id}?export=true`)}>
                              <Download className="h-4 w-4 mr-2" />
                              {t.exportPdf}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Generate Plan Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.selectEmployee}</DialogTitle>
            <DialogDescription>{t.selectEmployeeDesc}</DialogDescription>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto space-y-2 py-4">
            {dashboardData?.employees_without_plans?.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                {language === 'es' 
                  ? 'No hay empleados disponibles para generar planes' 
                  : 'No employees available to generate plans'}
              </p>
            ) : (
              dashboardData?.employees_without_plans?.map((employee) => (
                <div
                  key={employee.id}
                  onClick={() => setSelectedEmployee(employee)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedEmployee?.id === employee.id 
                      ? 'border-teal-500 bg-teal-50' 
                      : 'border-gray-200 hover:border-teal-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{employee.nombre}</p>
                      <p className="text-sm text-gray-500">{employee.email}</p>
                    </div>
                    {employee.departamento && (
                      <Badge variant="outline" className="text-xs">
                        {employee.departamento}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleGenerateIndividualPlan}
              disabled={!selectedEmployee || generatingPlan}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {generatingPlan ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t.generating}
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  {t.generate}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActionPlanDashboard;
